import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import { toast } from 'react-hot-toast';
import useToggle from '../../hooks/use-toggle';

const useAnnouncementService = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  const createAnnouncement = async (announcementDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.post(endpoints.announcements.create, announcementDto);
      toast.success(response.data.message || 'SMS announcement created successfully');
      if (onSuccess) onSuccess();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating SMS announcement');
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const updateAnnouncement = async (announcementDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.put(endpoints.announcements.update, announcementDto);
      toast.success(response.data.message || 'SMS announcement updated successfully');
      if (onSuccess) onSuccess();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating SMS announcement');
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const deleteAnnouncement = async (id, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.delete(`${endpoints.announcements.delete}${id}`);
      toast.success(response.data.message || 'SMS announcement deleted successfully');
      if (onSuccess) onSuccess();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting SMS announcement');
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const getAnnouncementById = async (id) => {
    try {
      const response = await axiosInstance.get(`${endpoints.announcements.get}${id}`);
      return response.data?.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching announcement');
      throw error;
    }
  };

  const getAnnouncementsByClass = async (classId, status = null) => {
    try {
      toggleLoading();
      const url = status
        ? `${endpoints.announcements.getByClass}${classId}?status=${status}`
        : `${endpoints.announcements.getByClass}${classId}`;
      const response = await axiosInstance.get(url);
      const data = response.data?.data;
      const list = Array.isArray(data) ? data : [];
      setAnnouncements(list);
      setTotalRecords(list.length);
      return list;
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Error fetching class announcements');
      }
      setAnnouncements([]);
      setTotalRecords(0);
      return [];
    } finally {
      toggleLoading();
    }
  };

  const fetchAnnouncements = async (page = 0, size = 10, status = null) => {
    try {
      toggleLoading();
      const url = status
        ? `${endpoints.announcements.getAll}?page=${page}&size=${size}&status=${status}`
        : `${endpoints.announcements.getAll}?page=${page}&size=${size}`;
      const response = await axiosInstance.get(url);
      const { data } = response.data;
      setTotalRecords(data?.records || 0);
      setAnnouncements(Array.isArray(data?.data) ? data.data : []);
      return data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching announcements');
      setAnnouncements([]);
      setTotalRecords(0);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const changeAnnouncementStatus = async (id, status, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.post(
        `${endpoints.announcements.status}?id=${id}&status=${status}`
      );
      toast.success(response.data.message || 'Announcement status updated');
      if (onSuccess) onSuccess();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating announcement status');
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const sendAnnouncementSms = async (announcementId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.post(`${endpoints.announcements.sendSms}${announcementId}`);
      toast.success(response.data.message || 'SMS sent successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send SMS to parents');
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const getAnnouncementsByParent = async (parentId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.announcements.getByParent}${parentId}`);
      const data = response.data?.data;
      const list = Array.isArray(data) ? data : [];
      setAnnouncements(list);
      setTotalRecords(list.length);
      return list;
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Error fetching parent announcements');
      }
      setAnnouncements([]);
      setTotalRecords(0);
      return [];
    } finally {
      toggleLoading();
    }
  };

  const getAllClassAnnouncements = async () => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(endpoints.announcements.getAllClasses);
      const data = response.data?.data;
      const list = Array.isArray(data) ? data : [];
      setAnnouncements(list);
      setTotalRecords(list.length);
      return list;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching school announcements');
      setAnnouncements([]);
      setTotalRecords(0);
      return [];
    } finally {
      toggleLoading();
    }
  };

  return {
    announcements,
    totalRecords,
    isLoading,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    getAnnouncementById,
    getAnnouncementsByClass,
    fetchAnnouncements,
    changeAnnouncementStatus,
    sendAnnouncementSms,
    getAnnouncementsByParent,
    getAllClassAnnouncements,
  };
};

export default useAnnouncementService;
